import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";
import { toQueryString, isNonEmptyVariable } from "@/util/common_function";

const buildPagedUrl = (baseUrl, pagination) => {
  const urlParam = toQueryString({
    page: pagination?.pageIndex,
    size: pagination?.pageSize,
  });
  return isNonEmptyVariable(urlParam) ? `${baseUrl}?${urlParam}` : baseUrl;
};

// POST bookings/search — the same endpoint Bookings' own report-list search
// and Document Control's Quick Search (Bookings) already hit. Response rows
// come back shaped for display (bookingNumber, dob, incarcerationDate,
// releaseDate, housingAgency, housingLocation) — except there's no "name"
// field; it's lastname/firstName/middleName/suffix separately, and has to
// be composed client-side. Bookings' own ReportList.jsx does this exact
// same composition for this exact same endpoint — mirrored here so both
// the results grid and the summary panel get a populated name instead of
// silently reading undefined.
const buildBookingName = (row) =>
  `${row?.lastname ? row.lastname + "," : ""} ${row?.firstName || ""}${
    row?.middleName ? " " + row.middleName : ""
  }${row?.suffix ? ", " + row.suffix : ""}`.trim();

export const searchBookingsModule = ({
  requestFilter = [],
  pagination,
} = {}) => {
  const url = buildPagedUrl(API_ENDPOINTS.bookingsSearch, pagination);
  return api.post(url, requestFilter).then((response) => ({
    total: response?.data?.total || 0,
    rows: (response?.data?.rows || []).map((row) => ({
      ...row,
      name: buildBookingName(row),
    })),
  }));
};

// GET bookings/bookingNumber/id/{bookingNumber} — the full booking detail
// (same endpoint Bookings' own onSearch uses to load a booking for
// editing). Returns a much richer object than the search-result row:
// nested bio[], arrestReports[], detentions[], etc. Used to populate the
// Booking Summary panel with authoritative, current data once a row is
// clicked, rather than just the row's own (already slightly stale/partial)
// search-result fields.
export const fetchBookingDetail = (bookingNumber) =>
  api.get(API_ENDPOINTS.getBookingByID + bookingNumber).then((r) => r?.data);

// POST warrant/search — the same endpoint Document Control's Quick Search
// (Warrants) already hits. Note: WarrantsService.buildWarrantRootPredicates
// throws IllegalArgumentException if no "searchType" criterion is present,
// so callers must always include one (SuperSearch's form always does, via
// its Search Type field defaulting to "Starts With").
export const searchWarrantsModule = ({
  requestFilter = [],
  pagination,
} = {}) => {
  const url = buildPagedUrl(API_ENDPOINTS.warrants.searchWarrants, pagination);
  return api.post(url, requestFilter).then((response) => ({
    total: response?.data?.total || 0,
    rows: response?.data?.rows || [],
  }));
};
